import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export type CreativePresetCategory = 'image_ad' | 'social_post' | 'video_ad';

export interface ImageAdPresetSettings {
  campaign: {
    offer: string;
    campaignGoal: string;
    audienceOverride: string;
    platform: string;
    toneOverride: string;
    adAngle: string;
    campaignNotes: string;
    creativeDirection: string;
    stylePackIds: string[];
    imageCount: number;
  };
  imageEngine: string;
  referenceImageData?: string | null;
  referenceImagePreview?: string | null;
}

export interface SocialPostPresetSettings {
  postMode: 'campaign' | 'daily';
  campaignForm?: Record<string, unknown>;
  dailyForm?: Record<string, unknown>;
  selectedProfileId?: string | null;
  dailySelectedProfileId?: string | null;
}

export interface VideoAdPresetSettings {
  campaign: {
    offer: string;
    campaignGoal: string;
    audienceOverride: string;
    platform: string;
    toneOverride: string;
    adAngle: string;
    campaignNotes: string;
    creativeDirection: string;
    stylePackIds: string[];
    imageCount: number;
  };
}

export type CreativePresetSettings = ImageAdPresetSettings | SocialPostPresetSettings | VideoAdPresetSettings;

export interface CreativePreset<TSettings = CreativePresetSettings> {
  id: string;
  user_id: string;
  business_profile_id: string | null;
  name: string;
  category: CreativePresetCategory;
  settings: TSettings;
  created_at: string;
  updated_at: string;
}

export function useCreativePresets<TSettings extends CreativePresetSettings = CreativePresetSettings>(category: CreativePresetCategory = 'image_ad') {
  const { user } = useAuth();
  const [presets, setPresets] = useState<CreativePreset<TSettings>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchPresets = useCallback(async () => {
    if (!user) {
      setPresets([]);
      setLoading(false);
      return;
    }
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    const { data, error: err } = await supabase
      .from('creative_presets')
      .select('*')
      .eq('user_id', user.id)
      .eq('category', category)
      .order('updated_at', { ascending: false });

    if (err) {
      setError(err.message);
    } else {
      setPresets((data ?? []) as CreativePreset<TSettings>[]);
    }
    setLoading(false);
    fetchingRef.current = false;
  }, [category, user]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  const createPreset = useCallback(
    async ({
      name,
      businessProfileId,
      settings,
    }: {
      name: string;
      businessProfileId?: string | null;
      settings: TSettings;
    }): Promise<{ data: CreativePreset<TSettings> | null; error: string | null }> => {
      if (!user) return { data: null, error: 'Not authenticated' };
      const { data, error: err } = await supabase
        .from('creative_presets')
        .insert({
          user_id: user.id,
          business_profile_id: businessProfileId ?? null,
          name: name.trim(),
          category,
          settings,
        })
        .select()
        .maybeSingle();

      if (err) return { data: null, error: err.message };
      await fetchPresets();
      return { data: data as CreativePreset<TSettings>, error: null };
    },
    [category, fetchPresets, user]
  );

  const deletePreset = useCallback(
    async (id: string): Promise<{ error: string | null }> => {
      if (!user) return { error: 'Not authenticated' };
      setPresets((prev) => prev.filter((preset) => preset.id !== id));
      const { error: err } = await supabase
        .from('creative_presets')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (err) {
        setError(err.message);
        await fetchPresets();
        return { error: err.message };
      }
      return { error: null };
    },
    [fetchPresets, user]
  );

  return { presets, loading, error, refetch: fetchPresets, createPreset, deletePreset };
}
